import { memo, ReactNode } from "react";
import { X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface CoreDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  preventOutsideClick?: boolean;
}

export const CoreDrawer = memo(
  ({ isOpen, onClose, title, description, children, className, preventOutsideClick = false }: CoreDrawerProps) => {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          showClose={false}
          aria-describedby={undefined}
          className={cn("w-full sm:!max-w-[80vw] flex flex-col h-screen-safe p-0 overflow-hidden", className)}
          onPointerDownOutside={(e) => {
            if (preventOutsideClick) e.preventDefault();
          }}
        >
          <div className="flex items-start justify-between p-8 border-b shrink-0">
            <div className="flex-1 min-w-0 pr-4">
              <SheetTitle className="text-2xl font-bold text-gray-900 leading-tight">{title}</SheetTitle>
              {description && (
                <SheetDescription className="text-sm text-gray-600 mt-2 leading-relaxed">
                  {description}
                </SheetDescription>
              )}
            </div>
            <button
              onClick={onClose}
              className="shrink-0 opacity-70 hover:opacity-100 transition-opacity focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] px-8 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] brand-scroll">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);

CoreDrawer.displayName = "CoreDrawer";
